import { type SyncSummary } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, EmptyState, PageHeader, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { customersQuery, partiesKeys, syncHousecall } from '../../shared/api/parties-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import { HousecallConnection } from './housecall-connection.js';

/**
 * Customers.
 *
 * A read-first list: customers are born from Housecall Pro sync and from
 * extraction, not typed in here. The page exists so a customer is a place,
 * somewhere their jobs, records and history hang together.
 */

const KIND_LABELS: Record<string, string> = {
  residential: 'Residential',
  commercial: 'Commercial',
  property_manager: 'Property manager',
  builder: 'Builder',
  government: 'Government',
};

function syncSentence(summary: SyncSummary): string {
  return (
    `Housecall Pro sync finished: ${String(summary.customers.created)} customers added, ` +
    `${String(summary.customers.updated)} refreshed` +
    (summary.customers.skipped > 0 ? ` (${String(summary.customers.skipped)} unnameable)` : '') +
    `; ${String(summary.jobs.created)} jobs added, ${String(summary.jobs.updated)} refreshed` +
    (summary.jobs.skipped > 0
      ? ` (${String(summary.jobs.skipped)} without a known customer)`
      : '') +
    (summary.invoices === undefined
      ? ''
      : `; ${String(summary.invoices.recorded)} completed invoices in the books as income`) +
    '.'
  );
}

export function CustomersPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const customers = useQuery(customersQuery(organizationId));
  const queryClient = useQueryClient();
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const canManage =
    session.organizations
      .find((entry) => entry.organization.id === organizationId)
      ?.permissions.includes('organization:manage') ?? false;

  const sync = useMutation({
    mutationFn: () => syncHousecall(organizationId),
    onSuccess: (summary) => {
      setSyncError(null);
      setSyncResult(syncSentence(summary));
      void queryClient.invalidateQueries({ queryKey: partiesKeys.customers(organizationId) });
      void queryClient.invalidateQueries({ queryKey: partiesKeys.jobs(organizationId) });
    },
    onError: (caught) => {
      setSyncResult(null);
      setSyncError(caught instanceof Error ? caught.message : 'The sync did not run.');
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        eyebrow="Business"
        title="Customers"
        description="Everyone the business works for. Customers arrive from Housecall Pro and from documents; this is where their history hangs together."
        action={
          <Button variant="secondary" onClick={() => sync.mutate()} isLoading={sync.isPending}>
            Sync Housecall Pro
          </Button>
        }
      />

      <HousecallConnection organizationId={organizationId} canManage={canManage} />

      {syncResult === null ? null : <Alert tone="success">{syncResult}</Alert>}
      {syncError === null ? null : <Alert tone="danger">{syncError}</Alert>}

      {customers.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : customers.isError ? (
        <Alert tone="danger" title="Customers could not be loaded">
          {customers.error.message}
        </Alert>
      ) : customers.data.length === 0 ? (
        <EmptyState
          icon="customer"
          title="No customers yet"
          body="They arrive on their own, from the documents you drop in, and from Housecall Pro when it syncs."
        />
      ) : (
        <Card>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="sticky top-0 z-10 bg-surface text-left text-xs uppercase tracking-wider text-content-subtle shadow-[inset_0_-1px_0_var(--color-border)]">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Kind</th>
                  <th className="px-5 py-3 font-semibold">Contact</th>
                  <th className="px-5 py-3 font-semibold">City</th>
                </tr>
              </thead>
              <tbody>
                {customers.data.map((customer) => (
                  <tr
                    key={customer.id}
                    className="border-b border-border transition-colors duration-[var(--duration-quick)] odd:bg-surface-muted/40 last:border-0 hover:bg-brand-50/60"
                  >
                    <td className="px-5 py-3">
                      <Link
                        to="/customers/$customerId"
                        params={{ customerId: customer.id }}
                        className="font-medium text-content hover:text-brand-700"
                      >
                        {customer.displayName}
                      </Link>
                      {customer.isTaxExempt ? (
                        <span className="ml-2 text-xs text-content-subtle">tax-exempt</span>
                      ) : null}
                    </td>
                    <td className="px-5 py-3 text-content-muted">
                      {KIND_LABELS[customer.kind] ?? customer.kind}
                    </td>
                    <td className="px-5 py-3 text-content-muted">
                      {customer.primaryPhone ?? customer.primaryEmail ?? '-'}
                    </td>
                    <td className="px-5 py-3 text-content-muted">{customer.billingCity ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
